import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead } from '@/components/ui/sortable-table-head';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  CheckCircle,
  XCircle,
  Clock,
  Ban,
  DollarSign,
  Users,
  TrendingUp,
  Settings,
  Eye,
  Edit,
  Trash2,
  Download,
  HelpCircle,
  Search,
  X,
} from 'lucide-react';
import { useTableSort } from '@/hooks/useTableSort';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useReferralCodes,
  useCreateReferralCode,
  useUpdateReferralCode,
  useDeleteReferralCode,
  useCommissions,
  useCommissionSummary,
  useApproveCommission,
  usePayouts,
  useCreatePayout,
  useProcessPayout,
  useCancelPayout,
  useAdminReferralStats,
  useReferralConfig,
  useUpdateReferralConfig,
  type ReferralCode,
  type Commission,
  type Payout,
} from '@/hooks/useReferrals';
import { format } from 'date-fns';

const ReferralManagement: React.FC = () => {
  const { data: stats } = useAdminReferralStats();
  const { data: config } = useReferralConfig();
  const [selectedTab, setSelectedTab] = useState('overview');

  return (
    <TooltipProvider>
      <div className="space-y-6">
        {/* Stats Overview */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Active Codes</CardTitle>
              <Users className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats?.total_active_codes || 0}</div>
              <p className="text-xs text-muted-foreground">
                {stats?.total_pending_codes || 0} pending approval
              </p>
            </CardContent>
          </Card>

          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="cursor-help">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-1">
                    Paying Referrals
                    <HelpCircle className="h-3 w-3" />
                  </CardTitle>
                  <TrendingUp className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{stats?.paying_referrals || 0}</div>
                  <p className="text-xs text-muted-foreground">
                    ${stats?.total_revenue_generated?.toFixed(2) || '0.00'} revenue
                  </p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-semibold mb-1">Paying Referrals (Converted)</p>
              <p className="text-sm">Number of unique users who signed up with a referral code AND made at least one payment. Users who signed up but haven't paid yet are not included.</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="cursor-help">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-1">
                    Pending Commissions
                    <HelpCircle className="h-3 w-3" />
                  </CardTitle>
                  <Clock className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    ${stats?.total_commissions_pending?.toFixed(2) || '0.00'}
                  </div>
                  <p className="text-xs text-muted-foreground">
                    ${stats?.total_commissions_paid?.toFixed(2) || '0.00'} paid
                  </p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-semibold mb-1">Pending + Approved Commissions</p>
              <p className="text-sm">Commissions that have been earned but not yet paid out to practitioners. Includes both newly created ("pending") and admin-verified ("approved") commissions waiting for payout batch.</p>
            </TooltipContent>
          </Tooltip>

          <Tooltip>
            <TooltipTrigger asChild>
              <Card className="cursor-help">
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-1">
                    Pending Payouts
                    <HelpCircle className="h-3 w-3" />
                  </CardTitle>
                  <DollarSign className="h-4 w-4 text-muted-foreground" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">
                    ${stats?.pending_payout_amount?.toFixed(2) || '0.00'}
                  </div>
                  <p className="text-xs text-muted-foreground">Ready to process</p>
                </CardContent>
              </Card>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs">
              <p className="font-semibold mb-1">Ready for Payout</p>
              <p className="text-sm">Total amount of approved commissions ready to be sent to practitioners via Stripe Connect. Create a payout batch to process these payments.</p>
            </TooltipContent>
          </Tooltip>
        </div>

      {/* Main Content Tabs */}
      <Tabs value={selectedTab} onValueChange={setSelectedTab}>
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="codes">Codes</TabsTrigger>
          <TabsTrigger value="commissions">Commissions</TabsTrigger>
          <TabsTrigger value="payouts">Payouts</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <OverviewTab />
        </TabsContent>

        <TabsContent value="codes" className="space-y-4">
          <ReferralCodesTab />
        </TabsContent>

        <TabsContent value="commissions" className="space-y-4">
          <CommissionsTab />
        </TabsContent>

        <TabsContent value="payouts" className="space-y-4">
          <PayoutsTab />
        </TabsContent>

        <TabsContent value="settings" className="space-y-4">
          <SettingsTab config={config} />
        </TabsContent>
      </Tabs>
      </div>
    </TooltipProvider>
  );
};

// ============================================
// Overview Tab
// ============================================

const OverviewTab: React.FC = () => {
  const { data: stats } = useAdminReferralStats();

  // Table sorting
  const { sortedData, requestSort, getSortIndicator } = useTableSort(
    stats?.top_performers || [],
    'total_revenue',
    'desc'
  );

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Top Performing Codes</CardTitle>
          <CardDescription>Referral codes generating the most revenue</CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  onSort={() => requestSort('code')}
                  sortDirection={getSortIndicator('code')}
                >
                  Code
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('practitioner_email')}
                  sortDirection={getSortIndicator('practitioner_email')}
                >
                  Practitioner
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('users_referred')}
                  sortDirection={getSortIndicator('users_referred')}
                  className="text-right"
                >
                  Referrals
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('total_revenue')}
                  sortDirection={getSortIndicator('total_revenue')}
                  className="text-right"
                >
                  Revenue
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('total_commission')}
                  sortDirection={getSortIndicator('total_commission')}
                  className="text-right"
                >
                  Commission
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.slice(0, 10).map((performer: any) => (
                <TableRow key={performer.code_id}>
                  <TableCell className="font-medium">{performer.code}</TableCell>
                  <TableCell>{performer.practitioner_email}</TableCell>
                  <TableCell className="text-right">{performer.users_referred}</TableCell>
                  <TableCell className="text-right">
                    ${performer.total_revenue?.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">
                    ${performer.total_commission?.toFixed(2)}
                  </TableCell>
                </TableRow>
              ))}
              {sortedData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="text-center text-muted-foreground">
                    No performance data yet
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================
// Referral Codes Tab
// ============================================

const ReferralCodesTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedCode, setSelectedCode] = useState<ReferralCode | null>(null);
  const [searchQuery, setSearchQuery] = useState('');

  const { data: codes, isLoading } = useReferralCodes(statusFilter);
  const createMutation = useCreateReferralCode();
  const updateMutation = useUpdateReferralCode();
  const deleteMutation = useDeleteReferralCode();

  // Filter codes based on search query
  const filteredCodes = (codes || []).filter(code => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      code.code.toLowerCase().includes(query) ||
      (code.practitioner?.full_name || '').toLowerCase().includes(query) ||
      (code.practitioner?.email || '').toLowerCase().includes(query) ||
      code.status.toLowerCase().includes(query)
    );
  });

  // Table sorting
  const { sortedData, requestSort, getSortIndicator } = useTableSort<ReferralCode>(
    filteredCodes,
    'total_revenue_generated',
    'desc'
  );

  const clearSearch = () => {
    setSearchQuery('');
  };

  const getStatusBadge = (status: string) => {
    const variants: Record<string, { variant: 'default' | 'secondary' | 'destructive' | 'outline'; icon: any }> = {
      active: { variant: 'default', icon: CheckCircle },
      pending: { variant: 'secondary', icon: Clock },
      suspended: { variant: 'outline', icon: Ban },
      revoked: { variant: 'destructive', icon: XCircle },
    };

    const config = variants[status] || variants.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className="h-3 w-3" />
        {status}
      </Badge>
    );
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Referral Codes</CardTitle>
              <CardDescription>Manage practitioner referral codes and approvals</CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All statuses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="suspended">Suspended</SelectItem>
                  <SelectItem value="revoked">Revoked</SelectItem>
                </SelectContent>
              </Select>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Code
              </Button>
            </div>
          </div>

          {/* Search Bar */}
          {(codes && codes.length > 0) && (
            <div className="flex items-center gap-2 mt-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search codes..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-8">
              {searchQuery ? (
                <>
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium text-muted-foreground">No codes found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your search query</p>
                </>
              ) : (
                <p className="text-muted-foreground">No referral codes found</p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    onSort={() => requestSort('code')}
                    sortDirection={getSortIndicator('code')}
                  >
                    Code
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('practitioner' as any)}
                    sortDirection={getSortIndicator('practitioner' as any)}
                  >
                    Practitioner
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('status')}
                    sortDirection={getSortIndicator('status')}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('total_uses')}
                    sortDirection={getSortIndicator('total_uses')}
                    className="text-right"
                  >
                    Uses
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('total_revenue_generated')}
                    sortDirection={getSortIndicator('total_revenue_generated')}
                    className="text-right"
                  >
                    Revenue
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('total_commission_earned')}
                    sortDirection={getSortIndicator('total_commission_earned')}
                    className="text-right"
                  >
                    Commission
                  </SortableTableHead>
                  <SortableTableHead sortable={false} className="text-right">
                    Actions
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((code) => (
                  <TableRow key={code.id}>
                    <TableCell className="font-mono font-medium">{code.code}</TableCell>
                    <TableCell>
                      {code.practitioner?.full_name || code.practitioner?.email}
                    </TableCell>
                    <TableCell>{getStatusBadge(code.status)}</TableCell>
                    <TableCell className="text-right">
                      {code.total_uses}
                      {code.custom_max_referrals && ` / ${code.custom_max_referrals}`}
                    </TableCell>
                    <TableCell className="text-right">
                      ${code.total_revenue_generated.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      ${code.total_commission_earned.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {code.status === 'pending' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              updateMutation.mutate({
                                codeId: code.id,
                                data: { status: 'active' },
                              })
                            }
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Approve
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setSelectedCode(code)}
                        >
                          <Edit className="h-3 w-3" />
                        </Button>
                        {code.total_uses === 0 && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => deleteMutation.mutate(code.id)}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Edit Code Dialog */}
      {selectedCode && (
        <EditCodeDialog
          code={selectedCode}
          open={!!selectedCode}
          onClose={() => setSelectedCode(null)}
          onSave={(data) => {
            updateMutation.mutate(
              {
                codeId: selectedCode.id,
                data,
              },
              {
                onSuccess: () => {
                  setSelectedCode(null);
                },
              }
            );
          }}
        />
      )}
    </div>
  );
};

// ============================================
// Edit Code Dialog Component
// ============================================

interface EditCodeDialogProps {
  code: ReferralCode;
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => void;
}

const EditCodeDialog: React.FC<EditCodeDialogProps> = ({
  code,
  open,
  onClose,
  onSave,
}) => {
  const [formData, setFormData] = useState({
    status: code.status,
    custom_discount_percentage: code.custom_discount_percentage || '',
    custom_commission_percentage: code.custom_commission_percentage || '',
    custom_max_referrals: code.custom_max_referrals || '',
    admin_notes: code.admin_notes || '',
    suspension_reason: code.suspension_reason || '',
  });

  const handleSave = () => {
    const updateData: any = {
      status: formData.status,
      admin_notes: formData.admin_notes || null,
    };

    // Only include custom fields if they have values
    if (formData.custom_discount_percentage) {
      updateData.custom_discount_percentage = parseFloat(formData.custom_discount_percentage as string);
    }
    if (formData.custom_commission_percentage) {
      updateData.custom_commission_percentage = parseFloat(formData.custom_commission_percentage as string);
    }
    if (formData.custom_max_referrals) {
      updateData.custom_max_referrals = parseInt(formData.custom_max_referrals as string);
    }
    if (formData.status === 'suspended' && formData.suspension_reason) {
      updateData.suspension_reason = formData.suspension_reason;
    }

    onSave(updateData);
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Edit Referral Code: {code.code}</DialogTitle>
          <DialogDescription>
            Update settings for this referral code
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Practitioner Info */}
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-sm font-medium">Practitioner</p>
            <p className="text-sm text-muted-foreground">
              {code.practitioner?.full_name || code.practitioner?.email}
            </p>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <Label htmlFor="status">Status</Label>
            <Select
              value={formData.status}
              onValueChange={(value) =>
                setFormData({ ...formData, status: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="suspended">Suspended</SelectItem>
                <SelectItem value="revoked">Revoked</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Custom Discount Percentage */}
          <div className="space-y-2">
            <Label htmlFor="discount">
              Custom Discount Percentage
              <span className="text-xs text-muted-foreground ml-2">
                (Leave empty to use default)
              </span>
            </Label>
            <Input
              id="discount"
              type="number"
              min="0"
              max="100"
              step="0.1"
              placeholder="e.g., 10"
              value={formData.custom_discount_percentage}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  custom_discount_percentage: e.target.value,
                })
              }
            />
          </div>

          {/* Custom Commission Percentage */}
          <div className="space-y-2">
            <Label htmlFor="commission">
              Custom Commission Percentage
              <span className="text-xs text-muted-foreground ml-2">
                (Leave empty to use tier-based commission)
              </span>
            </Label>
            <Input
              id="commission"
              type="number"
              min="0"
              max="100"
              step="0.1"
              placeholder="e.g., 20"
              value={formData.custom_commission_percentage}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  custom_commission_percentage: e.target.value,
                })
              }
            />
          </div>

          {/* Custom Max Referrals */}
          <div className="space-y-2">
            <Label htmlFor="max_referrals">
              Maximum Referrals
              <span className="text-xs text-muted-foreground ml-2">
                (Leave empty for unlimited)
              </span>
            </Label>
            <Input
              id="max_referrals"
              type="number"
              min="1"
              placeholder="e.g., 100"
              value={formData.custom_max_referrals}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  custom_max_referrals: e.target.value,
                })
              }
            />
          </div>

          {/* Suspension Reason (conditional) */}
          {formData.status === 'suspended' && (
            <div className="space-y-2">
              <Label htmlFor="suspension_reason">Suspension Reason</Label>
              <Textarea
                id="suspension_reason"
                placeholder="Explain why this code is being suspended..."
                value={formData.suspension_reason}
                onChange={(e) =>
                  setFormData({
                    ...formData,
                    suspension_reason: e.target.value,
                  })
                }
              />
            </div>
          )}

          {/* Admin Notes */}
          <div className="space-y-2">
            <Label htmlFor="admin_notes">Admin Notes</Label>
            <Textarea
              id="admin_notes"
              placeholder="Internal notes about this referral code..."
              value={formData.admin_notes}
              onChange={(e) =>
                setFormData({ ...formData, admin_notes: e.target.value })
              }
            />
          </div>

          {/* Current Stats */}
          <div className="grid grid-cols-3 gap-4 p-3 bg-muted rounded-lg">
            <div>
              <p className="text-xs text-muted-foreground">Uses</p>
              <p className="text-lg font-semibold">{code.total_uses}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Revenue</p>
              <p className="text-lg font-semibold">
                ${code.total_revenue_generated.toFixed(2)}
              </p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground">Commission</p>
              <p className="text-lg font-semibold">
                ${code.total_commission_earned.toFixed(2)}
              </p>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSave}>
            <Settings className="h-4 w-4 mr-2" />
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// ============================================
// Commissions Tab
// ============================================

const CommissionsTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const { data: commissions, isLoading } = useCommissions(undefined, statusFilter);
  const { data: summary } = useCommissionSummary();
  const approveMutation = useApproveCommission();

  // Filter commissions based on search query
  const filteredCommissions = (commissions || []).filter(commission => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (commission.practitioner?.full_name || '').toLowerCase().includes(query) ||
      (commission.practitioner?.email || '').toLowerCase().includes(query) ||
      (commission.referred_user?.full_name || '').toLowerCase().includes(query) ||
      (commission.referred_user?.email || '').toLowerCase().includes(query) ||
      commission.status.toLowerCase().includes(query)
    );
  });

  // Table sorting
  const { sortedData, requestSort, getSortIndicator } = useTableSort<Commission>(
    filteredCommissions,
    'transaction_date',
    'desc'
  );

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <div className="space-y-4">
      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pending</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${summary?.pending_commission?.toFixed(2) || '0.00'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Approved</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${summary?.approved_commission?.toFixed(2) || '0.00'}
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Paid</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              ${summary?.paid_commission?.toFixed(2) || '0.00'}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Commissions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Commission Ledger</CardTitle>
              <CardDescription>All commission transactions</CardDescription>
            </div>
            <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="approved">Approved</SelectItem>
                <SelectItem value="paid">Paid</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Search Bar */}
          {(commissions && commissions.length > 0) && (
            <div className="flex items-center gap-2 mt-4">
              <div className="relative flex-1 max-w-sm">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search commissions..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9 pr-9"
                />
                {searchQuery && (
                  <button
                    onClick={clearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2"
                  >
                    <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                  </button>
                )}
              </div>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="text-center py-8">Loading...</div>
          ) : sortedData.length === 0 ? (
            <div className="text-center py-8">
              {searchQuery ? (
                <>
                  <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-lg font-medium text-muted-foreground">No commissions found</p>
                  <p className="text-sm text-muted-foreground">Try adjusting your search query</p>
                </>
              ) : (
                <p className="text-muted-foreground">No commissions found</p>
              )}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <SortableTableHead
                    onSort={() => requestSort('transaction_date')}
                    sortDirection={getSortIndicator('transaction_date')}
                  >
                    Date
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('practitioner' as any)}
                    sortDirection={getSortIndicator('practitioner' as any)}
                  >
                    Practitioner
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('referred_user' as any)}
                    sortDirection={getSortIndicator('referred_user' as any)}
                  >
                    Referred User
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('gross_amount')}
                    sortDirection={getSortIndicator('gross_amount')}
                    className="text-right"
                  >
                    Amount
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('net_commission')}
                    sortDirection={getSortIndicator('net_commission')}
                    className="text-right"
                  >
                    Commission
                  </SortableTableHead>
                  <SortableTableHead
                    onSort={() => requestSort('status')}
                    sortDirection={getSortIndicator('status')}
                  >
                    Status
                  </SortableTableHead>
                  <SortableTableHead sortable={false} className="text-right">
                    Actions
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sortedData.map((commission) => (
                  <TableRow key={commission.id}>
                    <TableCell>
                      {format(new Date(commission.transaction_date), 'MMM d, yyyy')}
                    </TableCell>
                    <TableCell>
                      {commission.practitioner?.full_name || commission.practitioner?.email}
                    </TableCell>
                    <TableCell>
                      {commission.referred_user?.full_name || commission.referred_user?.email}
                    </TableCell>
                    <TableCell className="text-right">
                      ${commission.gross_amount.toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      ${commission.net_commission.toFixed(2)}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          commission.status === 'paid'
                            ? 'default'
                            : commission.status === 'approved'
                            ? 'secondary'
                            : 'outline'
                        }
                      >
                        {commission.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {commission.status === 'pending' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => approveMutation.mutate(commission.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// ============================================
// Payouts Tab
// ============================================

const PayoutsTab: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const { data: payouts, isLoading } = usePayouts(undefined, statusFilter);
  const processMutation = useProcessPayout();
  const cancelMutation = useCancelPayout();

  // Filter payouts based on search query
  const filteredPayouts = (payouts || []).filter(payout => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      (payout.practitioner?.full_name || '').toLowerCase().includes(query) ||
      (payout.practitioner?.email || '').toLowerCase().includes(query) ||
      payout.status.toLowerCase().includes(query)
    );
  });

  // Table sorting
  const { sortedData, requestSort, getSortIndicator } = useTableSort<Payout>(
    filteredPayouts,
    'scheduled_date',
    'desc'
  );

  const clearSearch = () => {
    setSearchQuery('');
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>Payouts</CardTitle>
            <CardDescription>Manage practitioner payouts</CardDescription>
          </div>
          <Select value={statusFilter || "all"} onValueChange={(value) => setStatusFilter(value === "all" ? "" : value)}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="processing">Processing</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="failed">Failed</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Search Bar */}
        {(payouts && payouts.length > 0) && (
          <div className="flex items-center gap-2 mt-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search payouts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 pr-9"
              />
              {searchQuery && (
                <button
                  onClick={clearSearch}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2"
                >
                  <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                </button>
              )}
            </div>
          </div>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-8">Loading...</div>
        ) : sortedData.length === 0 ? (
          <div className="text-center py-8">
            {searchQuery ? (
              <>
                <Search className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-lg font-medium text-muted-foreground">No payouts found</p>
                <p className="text-sm text-muted-foreground">Try adjusting your search query</p>
              </>
            ) : (
              <p className="text-muted-foreground">No payouts found</p>
            )}
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  onSort={() => requestSort('scheduled_date')}
                  sortDirection={getSortIndicator('scheduled_date')}
                >
                  Scheduled
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('practitioner' as any)}
                  sortDirection={getSortIndicator('practitioner' as any)}
                >
                  Practitioner
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('total_commission')}
                  sortDirection={getSortIndicator('total_commission')}
                  className="text-right"
                >
                  Amount
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('commission_count')}
                  sortDirection={getSortIndicator('commission_count')}
                  className="text-right"
                >
                  Commissions
                </SortableTableHead>
                <SortableTableHead
                  onSort={() => requestSort('status')}
                  sortDirection={getSortIndicator('status')}
                >
                  Status
                </SortableTableHead>
                <SortableTableHead sortable={false} className="text-right">
                  Actions
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((payout) => (
                <TableRow key={payout.id}>
                  <TableCell>
                    {format(new Date(payout.scheduled_date), 'MMM d, yyyy')}
                  </TableCell>
                  <TableCell>
                    {payout.practitioner?.full_name || payout.practitioner?.email}
                  </TableCell>
                  <TableCell className="text-right">
                    ${payout.total_commission.toFixed(2)}
                  </TableCell>
                  <TableCell className="text-right">{payout.commission_count}</TableCell>
                  <TableCell>
                    <Badge
                      variant={
                        payout.status === 'completed'
                          ? 'default'
                          : payout.status === 'failed'
                          ? 'destructive'
                          : 'secondary'
                      }
                    >
                      {payout.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-2">
                      {payout.status === 'pending' && (
                        <>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => processMutation.mutate(payout.id)}
                          >
                            Process
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              cancelMutation.mutate({ payoutId: payout.id })
                            }
                          >
                            Cancel
                          </Button>
                        </>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
};

// ============================================
// Settings Tab
// ============================================

const SettingsTab: React.FC<{ config: any }> = ({ config }) => {
  const updateMutation = useUpdateReferralConfig();
  const [formData, setFormData] = useState(config || {});

  React.useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const handleSave = () => {
    updateMutation.mutate(formData);
  };

  if (!config) return <div className="text-center py-8">Loading...</div>;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Referral System Settings</CardTitle>
        <CardDescription>Configure global referral and commission settings</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="default_discount_percentage">Default Discount %</Label>
            <Input
              id="default_discount_percentage"
              type="number"
              value={formData.default_discount_percentage || 0}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  default_discount_percentage: parseFloat(e.target.value),
                })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="default_commission_percentage">Default Commission %</Label>
            <Input
              id="default_commission_percentage"
              type="number"
              value={formData.default_commission_percentage || 0}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  default_commission_percentage: parseFloat(e.target.value),
                })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="vat_rate">VAT Rate %</Label>
            <Input
              id="vat_rate"
              type="number"
              value={formData.vat_rate || 0}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  vat_rate: parseFloat(e.target.value),
                })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="minimum_payout_threshold">Minimum Payout Threshold</Label>
            <Input
              id="minimum_payout_threshold"
              type="number"
              value={formData.minimum_payout_threshold || 0}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  minimum_payout_threshold: parseFloat(e.target.value),
                })
              }
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending ? 'Saving...' : 'Save Settings'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default ReferralManagement;
